import { Fragment } from 'react';
import { Box, Link, Tooltip, makeStyles, useTheme } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { CurrentTags } from '../../api/types';
import { cloudRunUrl, manifestYamlUrl, releaseUrl } from '../../links';
import { PromoteButton, CherryPickButton, HotfixButton } from './actions';

type Env = 'dev' | 'stg' | 'prd';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    overflowX: 'auto',
  },
  card: {
    flex: 1,
    minWidth: 220,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    // Fixed height so cards align even when a card has no action buttons (dev).
    minHeight: 48,
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  envLabel: {
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    fontSize: '0.85rem',
  },
  dot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  body: {
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  tagLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.hint,
  },
  tag: {
    fontFamily: 'monospace',
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  tagEmpty: {
    fontFamily: 'monospace',
    fontSize: '1rem',
    color: theme.palette.text.hint,
  },
  projectMono: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.hint,
  },
  links: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
  quickLink: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
  },
  quickLinkIcon: { fontSize: 12 },
  arrow: {
    alignSelf: 'center',
    color: theme.palette.text.hint,
    flexShrink: 0,
  },
}));

type Styles = ReturnType<typeof useStyles>;

const StageCard = ({
  env,
  componentName,
  projectName,
  tag,
  dotColor,
  classes,
  onDispatched,
  canOperate = true,
  deployprManaged = false,
}: {
  env: Env;
  componentName: string;
  projectName: string;
  tag: string | null;
  dotColor: string;
  classes: Styles;
  onDispatched?: () => void;
  canOperate?: boolean;
  deployprManaged?: boolean;
}) => (
  <Box className={classes.card}>
    <Box className={classes.cardHeader}>
      <span className={classes.dot} style={{ backgroundColor: dotColor }} />
      <span className={classes.envLabel}>{env}</span>
      <Box className={classes.headerActions}>
        {/* Promote moves stg → prd, so it lives on the stg card. */}
        {env === 'stg' && (
          <PromoteButton
            componentName={componentName}
            currentTag={tag}
            onDispatched={onDispatched}
            canOperate={canOperate}
          />
        )}
        {/* Hotfix targets a release branch, which only exists for stg/prd.
            Deploypr-managed projects open a hotfix PR via the deploypr
            workflow; legacy projects hotfix via cherry-pick. */}
        {env !== 'dev' &&
          (deployprManaged ? (
            <HotfixButton
              componentName={componentName}
              env={env}
              currentTag={tag}
              onDispatched={onDispatched}
              canOperate={canOperate}
            />
          ) : (
            <CherryPickButton
              componentName={componentName}
              projectName={projectName}
              tag={tag}
              onDispatched={onDispatched}
              canOperate={canOperate}
            />
          ))}
      </Box>
    </Box>
    <Box className={classes.body}>
      <span className={classes.tagLabel}>deployed</span>
      {tag ? (
        <span className={classes.tag}>{tag}</span>
      ) : (
        <span className={classes.tagEmpty}>—</span>
      )}
      {projectName !== componentName && (
        <span className={classes.projectMono}>{projectName}</span>
      )}
      <Box className={classes.links}>
        <Tooltip title={`Open the ${env} Cloud Run service in the GCP console`}>
          <Link
            href={cloudRunUrl(projectName, env)}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.quickLink}
          >
            service <OpenInNewIcon className={classes.quickLinkIcon} />
          </Link>
        </Tooltip>
        <Tooltip title={`Open ${env} manifest.yaml on GitHub`}>
          <Link
            href={manifestYamlUrl(projectName, env)}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.quickLink}
          >
            manifest <OpenInNewIcon className={classes.quickLinkIcon} />
          </Link>
        </Tooltip>
        {tag && tag !== 'latest' && (
          <Tooltip title={`Open GitHub Release for ${tag}`}>
            <Link
              href={releaseUrl(projectName, tag)}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.quickLink}
            >
              release <OpenInNewIcon className={classes.quickLinkIcon} />
            </Link>
          </Tooltip>
        )}
      </Box>
    </Box>
  </Box>
);

/**
 * Horizontal dev → stg → prd pipeline showing the currently-deployed tag per
 * environment, with per-stage Promote / Pick actions. Mirrors the deploy
 * pipeline overview; the open PR sections below show what is pending.
 */
export const PipelineStages = ({
  componentName,
  projectName,
  currentTags,
  onDispatched,
  canOperate = true,
  deployprManaged = false,
}: {
  componentName: string;
  projectName: string;
  currentTags: CurrentTags;
  onDispatched?: () => void;
  canOperate?: boolean;
  /** True when the project is managed by Go mergeops (deploypr). */
  deployprManaged?: boolean;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const envs: Env[] = ['dev', 'stg', 'prd'];
  const dotColor = (env: Env): string => {
    if (env === 'prd') return theme.palette.success?.main ?? '#2e7d32';
    if (env === 'stg') return theme.palette.warning?.main ?? '#ed6c02';
    return theme.palette.text.hint;
  };

  return (
    <Box className={classes.row}>
      {envs.map((env, i) => (
        <Fragment key={env}>
          {i > 0 && (
            <ArrowForwardIcon className={classes.arrow} fontSize="small" />
          )}
          <StageCard
            env={env}
            componentName={componentName}
            projectName={projectName}
            tag={currentTags[env]}
            dotColor={dotColor(env)}
            classes={classes}
            onDispatched={onDispatched}
            canOperate={canOperate}
            deployprManaged={deployprManaged}
          />
        </Fragment>
      ))}
    </Box>
  );
};
